import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaHome,
  FaArrowLeft,
  FaSearch,
  FaRocket,
  FaShieldAlt,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";

const NotFound = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const floatingVariants = {
    hidden: { y: 0 },
    visible: {
      y: [0, -15, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "1rem" : "2rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "absolute",
          top: "10%",
          left: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "absolute",
          bottom: "10%",
          right: "-5%",
          width: "350px",
          height: "350px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          maxWidth: "600px",
          width: "100%",
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "32px",
          padding: isMobile ? "2rem" : "3rem",
          textAlign: "center",
          border: `1px solid ${getBorderColor()}`,
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Animated gradient border top */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            borderRadius: "32px 32px 0 0",
          }}
        />

        {/* Animated 404 Number */}
        <motion.div
          variants={itemVariants}
          style={{ position: "relative", marginBottom: "1rem" }}
        >
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: isMobile ? "6rem" : "8rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
                letterSpacing: "0.1em",
              }}
            >
              404
            </div>
          </motion.div>
        </motion.div>

        {/* Animated Illustration / Lost character */}
        <motion.div
          variants={floatingVariants}
          initial="hidden"
          animate="visible"
          style={{
            marginBottom: "1.5rem",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: isMobile ? "120px" : "150px",
              height: isMobile ? "120px" : "150px",
              background: `linear-gradient(135deg, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.05)"}, transparent)`,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {/* Astronaut / Lost character SVG */}
            <svg
              width={isMobile ? "100" : "120"}
              height={isMobile ? "100" : "120"}
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Helmet */}
              <circle
                cx="60"
                cy="50"
                r="30"
                fill="#8b5cf6"
                fillOpacity="0.2"
                stroke="#8b5cf6"
                strokeWidth="2"
              />
              {/* Face */}
              <circle cx="60" cy="50" r="20" fill="#8b5cf6" fillOpacity="0.3" />
              {/* Eyes */}
              <circle cx="50" cy="45" r="3" fill="#8b5cf6" />
              <circle cx="70" cy="45" r="3" fill="#8b5cf6" />
              {/* Mouth */}
              <path
                d="M52 58 Q60 65 68 58"
                stroke="#8b5cf6"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
              {/* Body */}
              <rect
                x="45"
                y="75"
                width="30"
                height="25"
                rx="5"
                fill="#8b5cf6"
                fillOpacity="0.2"
                stroke="#8b5cf6"
                strokeWidth="2"
              />
              {/* Arms */}
              <path
                d="M45 80 L30 70"
                stroke="#8b5cf6"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M75 80 L90 70"
                stroke="#8b5cf6"
                strokeWidth="3"
                strokeLinecap="round"
              />
              {/* Question Mark */}
              <text
                x="90"
                y="40"
                fontSize="24"
                fill="#f59e0b"
                fontWeight="bold"
              >
                ?
              </text>
              {/* Small stars */}
              <circle cx="25" cy="30" r="2" fill="#8b5cf6" />
              <circle cx="95" cy="25" r="2" fill="#8b5cf6" />
              <circle cx="35" cy="80" r="1.5" fill="#3b82f6" />
              <circle cx="85" cy="85" r="1.5" fill="#3b82f6" />
            </svg>
          </div>
        </motion.div>

        <motion.h2
          variants={itemVariants}
          style={{
            fontSize: isMobile ? "1.5rem" : "2rem",
            fontWeight: "bold",
            color: getTextColor(),
            marginBottom: "0.5rem",
          }}
        >
          Oops! Page Not Found
        </motion.h2>

        <motion.p
          variants={itemVariants}
          style={{
            color: getTextSecondary(),
            marginBottom: "2rem",
            fontSize: isMobile ? "0.9rem" : "1rem",
            lineHeight: "1.6",
            maxWidth: "400px",
            margin: "0 auto 2rem auto",
          }}
        >
          The page you are looking for might have been removed, had its name
          changed, or is temporarily unavailable.
        </motion.p>

        {/* Search suggestion */}
        <motion.div
          variants={itemVariants}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            marginBottom: "2rem",
            padding: "0.5rem 1rem",
            background: isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)",
            borderRadius: "40px",
            width: "fit-content",
            margin: "0 auto 2rem auto",
          }}
        >
          <FaSearch size={12} color="#8b5cf6" />
          <span style={{ fontSize: "0.75rem", color: getTextSecondary() }}>
            Try going back to the homepage or using the navigation menu
          </span>
        </motion.div>

        <motion.div
          variants={itemVariants}
          style={{
            display: "flex",
            gap: "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
              to="/"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.8rem 1.8rem",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                textDecoration: "none",
                borderRadius: "40px",
                fontSize: "0.9rem",
                fontWeight: "600",
                boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                transition: "all 0.3s",
              }}
            >
              <FaHome /> Go to Homepage
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
              to="/dashboard"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.8rem 1.8rem",
                background: isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)",
                color: getTextColor(),
                textDecoration: "none",
                borderRadius: "40px",
                fontSize: "0.9rem",
                fontWeight: "500",
                border: `1px solid ${getBorderColor()}`,
                transition: "all 0.3s",
              }}
            >
              <FaArrowLeft /> Go to Dashboard
            </Link>
          </motion.div>
        </motion.div>

        {/* Fun Fact / Motivational Message */}
        <motion.div
          variants={itemVariants}
          style={{
            marginTop: "2rem",
            padding: "0.75rem",
            background: isDarkMode
              ? "rgba(139,92,246,0.05)"
              : "rgba(139,92,246,0.03)",
            borderRadius: "16px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            <FaRocket size={14} color="#8b5cf6" />
            <span style={{ fontSize: "0.7rem", color: getTextSecondary() }}>
              Don't worry, even astronauts get lost sometimes!
            </span>
            <FaShieldAlt size={12} color="#10b981" />
          </div>
        </motion.div>
      </motion.div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default NotFound;
